import React, { useEffect, useState } from 'react';
import { Stethoscope, BarChart, Check, AlertCircle, XCircle, Shield, Sparkles, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getFileURL } from '../../utils/fileURL';

// Presentational only — all analytics arrive via props from the real-time admin
// dashboard socket (AdminDashboard owns the params/data). This component just
// renders the greeting, the doctor/patient counts and the booking analytics
// cards; the booking period control is built by AdminDashboard and passed in as
// `periodSelector`.
const AdminWelcomeSection = ({ counts, booking, loading }) => {
    const { t } = useTranslation('dashboard');
    const [currentTime, setCurrentTime] = useState(new Date());
    const { admin } = useSelector((state) => state.admin);
    const analyticsLoading = loading;
    const countLoading = loading;
    const countData = counts;

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const getTimeGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return t('dashboard.greetings.good_morning');
        if (hour < 17) return t('dashboard.greetings.good_afternoon');
        return t('dashboard.greetings.good_evening');
    };

    const cards = [
        { id: 'booked', label: t('dashboard.analytics_total'), icon: BarChart, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
        { id: 'completed', label: t('dashboard.analytics_completed'), icon: Check, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
        { id: 'missedout', label: t('dashboard.analytics_missed_out'), icon: AlertCircle, bg: 'bg-gradient-to-r from-[#6B7280] to-[#4B5563]' },
        { id: 'canceled', label: t('dashboard.analytics_canceled'), icon: XCircle, bg: 'bg-gradient-to-r from-[#9CA3AF] to-[#6B7280]' },
    ];

    // Map the socket booking payload to the card shape; default to zeros.
    const getBookingData = () => {
        if (booking?.counts) {
            return {
                booked: { count: booking.total || 0 },
                completed: { count: booking.counts.Completed || 0 },
                missedout: { count: booking.counts.NoShow || 0 },
                canceled: { count: booking.counts.Canceled || 0 },
            };
        }
        return {
            booked: { count: 0 },
            completed: { count: 0 },
            missedout: { count: 0 },
            canceled: { count: 0 },
        };
    };

    const currentBookingData = getBookingData();

    return (
        <div className="grid grid-cols-1 xl:grid-cols-[24%_20%_auto] gap-4 w-full max-w-full">
            {/* Welcome Card */}
            <motion.div
                data-testid="welcome-left-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative bg-gradient-to-br from-[#328e8e] to-[#4ABDBD] rounded-xl p-4 sm:p-6 md:p-8 shadow-md backdrop-blur-sm overflow-hidden flex flex-col justify-center"
            >
                {/* Background decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white/5 rounded-full"></div>

                {/* Sparkle effects */}
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute top-4 right-4"
                >
                    <Sparkles className="w-6 h-6 text-white/40" />
                </motion.div>

                <div data-testid="welcome-content" className="relative z-10 space-y-4 sm:space-y-6">
                    {/* Header with greeting and time */}
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="text-center"
                    >
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
                            <p data-testid="welcome-greeting-text" className="text-white/90 text-xs sm:text-sm font-medium">
                                {getTimeGreeting()}
                            </p>
                        </div>
                        <h1 data-testid="welcome-admin-heading" className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                            {t('dashboard.welcome_admin')}
                        </h1>
                    </motion.div>

                    {/* Admin info section */}
                    <motion.div
                        data-testid="admin-info-section"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="flex flex-col items-center space-y-4"
                    >
                        <div
                            data-testid="admin-avatar-container"
                            className="relative"
                        >
                            <div
                                data-testid="admin-avatar"
                                className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full border-4 border-white/30 shadow-xl bg-white flex items-center justify-center"
                            >
                                {admin?.image ? (
                                    <img
                                        data-testid="admin-image"
                                        src={getFileURL(admin.image)}
                                        alt={admin?.name || t('dashboard.admin_alt', 'Admin')}
                                        className="w-full h-full object-cover rounded-full"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    data-testid="admin-avatar-initial"
                                    className="w-full h-full rounded-full bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] flex items-center justify-center text-white text-xl sm:text-2xl md:text-3xl font-bold"
                                    style={{ display: admin?.image ? 'none' : 'flex' }}
                                >
                                    {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
                                </div>
                                <div
                                    data-testid="shield-badge"
                                    className="absolute -bottom-2 -right-2 bg-white rounded-full p-1.5 sm:p-2 border-2 border-[#4ABDBD] shadow-lg"
                                >
                                    <Shield
                                        data-testid="shield-icon"
                                        className="w-3 h-3 sm:w-4 sm:h-4 text-[#4ABDBD]"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            data-testid="admin-details"
                            className="text-center"
                        >
                            <h2
                                data-testid="admin-name"
                                className="text-lg sm:text-xl md:text-2xl font-bold text-white"
                            >
                                {admin?.name}
                            </h2>
                        </div>
                    </motion.div>
                </div>
            </motion.div>

            {/* Doctors & Patients Section */}
            <div className="grid grid-rows-2 gap-4">
                {/* Doctors */}
                <motion.div
                    data-testid="dashboard-doctors-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="relative bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200 shadow-md overflow-hidden"
                >
                    <div className="relative z-10 flex flex-col h-full justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <h3 data-testid="dashboard-doctors-heading" className="text-xs sm:text-sm md:text-base font-bold text-gray-800 tracking-wide">
                                {t('dashboard.doctors')}
                            </h3>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-5">
                            <div className="p-2 sm:p-3 md:p-4">
                                <Stethoscope data-testid="dashboard-doctors-icon" className="w-5 sm:w-6 md:w-7 h-5 sm:h-6 md:h-7 text-[#4ABDBD]" />
                            </div>
                            <div className="flex flex-col">
                                <p data-testid="dashboard-doctors-count" className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 tracking-tight">
                                    {countLoading ? (
                                        <span className="inline-flex items-center" data-testid="dashboard-doctors-loading">
                                            <span className="w-12 h-6 bg-gray-200 rounded animate-pulse"></span>
                                        </span>
                                    ) : (
                                        countData?.totalDoctors || 0
                                    )}
                                </p>
                                <p data-testid="dashboard-doctors-label" className="text-xs sm:text-xs md:text-xs text-gray-500 font-medium">{t('dashboard.active_doctors')}</p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Patients */}
                <motion.div
                    data-testid="dashboard-patients-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="relative bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200 shadow-md overflow-hidden"
                >
                    <div className="relative z-10 flex flex-col h-full justify-between">
                        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <h3 data-testid="dashboard-patients-heading" className="text-xs sm:text-sm md:text-base font-bold text-gray-800 tracking-wide">
                                {t('dashboard.patients')}
                            </h3>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-5">
                            <div className="p-2 sm:p-3 md:p-4">
                                <Users data-testid="dashboard-patients-icon" className="w-5 sm:w-6 md:w-7 h-5 sm:h-6 md:h-7 text-[#4ABDBD]" />
                            </div>
                            <div className="flex flex-col">
                                <p data-testid="dashboard-patients-count" className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 tracking-tight">
                                    {countLoading ? (
                                        <span className="inline-flex items-center" data-testid="dashboard-patients-loading">
                                            <span className="w-12 h-6 bg-gray-200 rounded animate-pulse"></span>
                                        </span>
                                    ) : (
                                        countData?.totalPatients || 0
                                    )}
                                </p>
                                <p data-testid="dashboard-patients-label" className="text-xs sm:text-xs md:text-xs text-gray-500 font-medium">{t('dashboard.active_patients')}</p>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Booking Analytics */}
            <motion.div
                data-testid="dashboard-booking-analytics-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="relative bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 sm:p-4 md:p-6 border border-gray-200 shadow-md overflow-hidden"
            >
                <div className="relative z-10 flex flex-col h-full">
                    <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-5">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <h3 data-testid="dashboard-booking-analytics-heading" className="text-xs sm:text-sm md:text-base font-bold text-gray-800 tracking-wide flex items-center">
                                <BarChart data-testid="dashboard-booking-analytics-icon" className="w-3 sm:w-4 h-3 sm:h-4 text-[#4ABDBD] mr-2" />
                                {t('dashboard.booking_analytics')}
                            </h3>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 flex-1">
                        {cards.map((card, index) => (
                            <motion.div
                                key={card.id}
                                data-testid={`dashboard-analytics-card-${card.id}`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 * index }}
                                className="relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-3 sm:p-4 md:p-5 border border-gray-200 shadow-sm flex flex-col justify-center w-full"
                            >
                                <div className="relative z-10 flex items-center gap-2 sm:gap-3 md:gap-4">
                                    <div className="relative flex-shrink-0">
                                        <div className={`p-2 sm:p-2.5 md:p-3 rounded-full ${card.bg} shadow-md`}>
                                            <card.icon data-testid={`dashboard-analytics-card-icon-${card.id}`} className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p data-testid={`dashboard-analytics-card-count-${card.id}`} className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 tracking-tight">
                                            {analyticsLoading ? (
                                                <span className="inline-flex items-center" data-testid={`dashboard-analytics-card-loading-${card.id}`}>
                                                    <span className="w-10 sm:w-12 md:w-14 h-5 sm:h-6 bg-gray-200 rounded animate-pulse"></span>
                                                </span>
                                            ) : (
                                                currentBookingData[card.id]?.count.toLocaleString()
                                            )}
                                        </p>
                                        <p data-testid={`dashboard-analytics-card-label-${card.id}`} className="text-[0.65rem] sm:text-xs md:text-sm lg:text-base text-gray-600 font-medium mt-0.5 sm:mt-1">{card.label}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AdminWelcomeSection;